<template>
  <div class="api-key-usage-tiles">
    <ApiKeyUsageTile
      v-for="tile in tiles"
      :key="tile.label"
      :label="tile.label"
      :value="tile.value"
      :unit="tile.unit"
      :footnote="tile.footnote"
      :progress="tile.progress"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { ApiKeyLimits } from '@/types/api_keys';
import { usageColorFor } from '@/utils/usageColor';

import ApiKeyUsageTile from './ApiKeyUsageTile.vue';

const { limits } = defineProps<{
  limits: ApiKeyLimits;
}>();

const dailyUsedPercent = computed(() => {
  if (!limits.daily_limit) return 0;
  return Math.min(
    100,
    Math.round((limits.daily_usage_count / limits.daily_limit) * 100),
  );
});

const tiles = computed(() => [
  {
    label: 'Requests Today',
    value: limits.daily_usage_count.toLocaleString(),
    unit: `/ ${limits.daily_limit.toLocaleString()}`,
    footnote: `${dailyUsedPercent.value}% of daily limit used`,
    progress: {
      percent: dailyUsedPercent.value,
      color: usageColorFor(dailyUsedPercent.value),
    },
  },
  {
    label: 'Per-Minute Limit',
    value: limits.requests_per_minute.toLocaleString(),
    unit: 'req / min',
    footnote: 'Shared across all keys in your organization',
  },
  {
    label: 'Daily Limit',
    value: limits.daily_limit.toLocaleString(),
    unit: 'req / day',
    footnote: 'Resets 00:00 UTC',
  },
]);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-key-usage-tiles {
  @apply grid grid-cols-1 md:grid-cols-3 gap-16;
}
</style>
